// components/AddressesSection.tsx
import React, { useState } from 'react';
import { useRoles } from '../useRoles';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import HomeIcon from "@mui/icons-material/Home";
import GroupIcon from "@mui/icons-material/Group";
import { CustomerAddress } from "../types/address";
import { ValidationErrors } from '../types/customer';
import { colors, borderRadius, typography, components, elevation } from '../theme';

interface AddressesSectionProps {
  addresses: CustomerAddress[];
  validationErrors: ValidationErrors;
  disabled?: boolean; // Add disabled prop to make the section read-only
  hideGroupButton?: boolean; // Hide the group icon button (used in care home details)
  onAddAddress: () => void;
  onEditAddress: (address: CustomerAddress) => void;
  onRemoveAddress: (index: number) => void;
  onUpdateAddress: (index: number, updates: Partial<CustomerAddress>) => void;
  onCareHomeClick?: () => void; 
  onConvertToSharedSpace?: (address: CustomerAddress) => void; // Handler for converting address to shared space
}

export const AddressesSection: React.FC<AddressesSectionProps> = ({
  addresses,
  validationErrors,
  disabled = false,
  hideGroupButton = false,
  onAddAddress,
  onEditAddress,
  onRemoveAddress,
  onUpdateAddress,
  onCareHomeClick,
  onConvertToSharedSpace,
}) => {
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [selectedAddressForConversion, setSelectedAddressForConversion] = useState<CustomerAddress | null>(null);

  const roles = useRoles();
  const isPrivileged = roles.includes('PrivilegedUsers');
  const isSuperUser = roles.includes('SuperUser');
  const noPermissionMessage = "You do not have permission to access this option. Please contact your manager or supervisor for advice.";

  const handleConvertClick = (address: CustomerAddress) => {
    setSelectedAddressForConversion(address);
    setWarningDialogOpen(true);
  };

  const handleConfirmConvert = () => {
    if (selectedAddressForConversion && onConvertToSharedSpace) {
      onConvertToSharedSpace(selectedAddressForConversion);
    }
    setWarningDialogOpen(false);
    setSelectedAddressForConversion(null);
  };

  const handleCancelConvert = () => {
    setWarningDialogOpen(false);
    setSelectedAddressForConversion(null);
  };
  return (
    <Paper sx={{ p: 3, height: "100%" }} elevation={elevation.paper}>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: validationErrors.addresses ? 1 : 2,
          }}
        >
          <Typography variant="h6">
            Addresses
            {disabled && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Shared Address)
              </Typography>
            )}
          </Typography>
          {!disabled && (
            <Tooltip title="Add Address">
              <IconButton onClick={onAddAddress} size="small">
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {validationErrors.addresses && (
          <Typography
            color="error"
            variant="caption"
            sx={{ display: "block" }}
          >
            {validationErrors.addresses}
          </Typography>
        )}
      </Box>

      {!!addresses.length && (
        <Box>
          {addresses.map((address, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #e0e0e0",
                borderRadius: borderRadius.large,
                px: 2,
                py: 0,
                mt: 0.8,
                mb: 0.8,
                backgroundColor: address.isActive ? colors.background.sidebarGlass : colors.background.inactive,
                opacity: address.isActive ? 1 : 0.6,
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: borderRadius.medium,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  cursor: address.fromCareHome && onCareHomeClick ? 'pointer' : 'default',
                  '&:hover': address.fromCareHome && onCareHomeClick ? {
                    backgroundColor: colors.interaction.hover,
                  } : {},
                }}
                onClick={address.fromCareHome && onCareHomeClick ? onCareHomeClick : undefined}
              >
                <Tooltip 
                  title={address.fromCareHome ? 'Click to view shared address details' : ''}
                  placement="top"
                >
                  <HomeIcon
                    sx={{
                      fontSize: components.icon.large.width,
                      color: address.fromCareHome 
                        ? colors.status.warning 
                        : colors.text.muted,
                      width: 30,
                      height: 30,
                    }}
                  />
                </Tooltip>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ mr: 1 }} />

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: typography.fontWeight.medium,
                    fontSize: typography.fontSize.small,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {[
                    address.street,
                    address.nameNumber,
                    address.postcode,
                    address.town,
                    address.district,
                    address.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={address.isActive}
                      disabled={disabled}
                      onChange={(e) =>
                        onUpdateAddress(index, {
                          isActive: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography variant="caption">Active</Typography>
                  }
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      marginLeft: '4px'
                    }
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={address.isPrimary}
                      disabled={disabled}
                      onChange={(e) =>
                        onUpdateAddress(index, {
                          isPrimary: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography variant="caption">Primary</Typography>
                  }
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      marginLeft: '4px'
                    }
                  }}
                />

                {!disabled && (
                  <>
                    {!hideGroupButton && (
                      <Tooltip title={!isPrivileged ? noPermissionMessage : address.isPrimary ? "Add customer to this address" : "Only available for primary addresses"}>
                        <span>
                          <IconButton
                            onClick={() => handleConvertClick(address)}
                            disabled={!address.isPrimary || !isPrivileged}
                            sx={{ 
                              p: 0.3, 
                              mr: 0.1,
                              opacity: address.isPrimary && isPrivileged ? 1 : 0.5,
                            }}
                          >
                            <GroupIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {isSuperUser && (
                      <IconButton
                        onClick={() => onEditAddress(address)}
                        sx={{ p: 0.3, mr: 0.1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label="delete"
                      onClick={() => onRemoveAddress(index)}
                      className="delete-button"
                      sx={{
                        "&:hover": { color: "error.main" },
                        p: 0.3,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Warning Dialog */}
      <Dialog
        open={warningDialogOpen}
        onClose={handleCancelConvert}
        aria-labelledby="warning-dialog-title"
        aria-describedby="warning-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="warning-dialog-title">Warning</DialogTitle>
        <DialogContent>
          <DialogContentText id="warning-dialog-description" sx={{ mb: 2 }}>
            Be sure to save any changes to the customer before you continue.
          </DialogContentText>
          <DialogContentText>
            This address will be converted into a shared address and will become the primary address of the current customer and the next customer you add!
          </DialogContentText>
          <DialogContentText>
            Details from the current customer will be used to populate contact information for the shared address (can be changed later).
          </DialogContentText>
          <DialogContentText>
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConvert} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmConvert} color="primary" variant="contained" autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};